export type UserRole = "customer" | "admin";

export interface ImpersonationInfo {
  active: boolean;
  readOnly: boolean;
  adminUserId: string;
  adminEmail: string;
  adminName: string;
}

export interface AuthenticatedUser {
  id: string;
  email: string;
  name: string;
  company?: string;
  role: UserRole;
  mustChangePassword: boolean;
  impersonation: ImpersonationInfo | null;
}

export interface AuthSession {
  token: string | null;
  user: AuthenticatedUser;
}

export interface AccountSetupState {
  customerName: string;
  customerEmail: string;
  projectTitle: string | null;
  orderAcceptedAt: string | null;
  paymentStatus: string | null;
  isPaymentConfirmed: boolean;
  mustSetPassword: boolean;
}

class AuthApiError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = "AuthApiError";
    this.status = status;
  }
}

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || "/api").replace(/\/+$/, "");
const LOGIN_PATH = import.meta.env.VITE_AUTH_LOGIN_PATH || "/auth/login";
const CURRENT_USER_PATH = import.meta.env.VITE_AUTH_ME_PATH || "/auth/me";
const SESSION_PATH = import.meta.env.VITE_AUTH_SESSION_PATH || "/auth/session";
const PASSWORD_PATH = import.meta.env.VITE_AUTH_PASSWORD_PATH || "/auth/password";
const ADMIN_IMPERSONATE_BASE_PATH =
  import.meta.env.VITE_AUTH_ADMIN_IMPERSONATE_BASE_PATH || "/admin/customer-accounts";
const ACCOUNT_SETUP_REQUEST_PATH =
  import.meta.env.VITE_AUTH_ACCOUNT_SETUP_REQUEST_PATH || "/public/account-setup/request";
const ACCOUNT_SETUP_TOKEN_PATH =
  import.meta.env.VITE_AUTH_ACCOUNT_SETUP_TOKEN_PATH || "/public/account-setup/token";
const ACCOUNT_SETUP_COMPLETE_PATH =
  import.meta.env.VITE_AUTH_ACCOUNT_SETUP_COMPLETE_PATH || "/public/account-setup/complete";

const AUTH_TOKEN_STORAGE_KEY = "mutzur_auth_token";
const AUTH_USER_STORAGE_KEY = "mutzur_auth_user";
const AUTH_IMPERSONATION_BACKUP_TOKEN_STORAGE_KEY = "mutzur_auth_impersonation_backup_token";
const AUTH_IMPERSONATION_BACKUP_USER_STORAGE_KEY = "mutzur_auth_impersonation_backup_user";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function joinUrl(baseUrl: string, path: string) {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
}

function getAuthEndpoint(path: string) {
  return joinUrl(API_BASE_URL, path);
}

function getErrorMessage(payload: unknown, fallback: string) {
  if (!isRecord(payload)) {
    return fallback;
  }

  const message = payload.message || payload.error;
  if (typeof message === "string" && message.trim()) {
    return message;
  }

  return fallback;
}

function mapRole(rawRole: unknown): UserRole {
  if (typeof rawRole !== "string") {
    return "customer";
  }

  const normalizedRole = rawRole.trim().toLowerCase();
  if (
    normalizedRole === "admin" ||
    normalizedRole === "administrator" ||
    normalizedRole === "super_admin" ||
    normalizedRole === "superadmin"
  ) {
    return "admin";
  }

  return "customer";
}

function normalizeImpersonation(rawValue: unknown): ImpersonationInfo | null {
  if (!isRecord(rawValue) || rawValue.active !== true) {
    return null;
  }

  return {
    active: true,
    readOnly: rawValue.readOnly !== false,
    adminUserId: typeof rawValue.adminUserId === "string" ? rawValue.adminUserId : "",
    adminEmail: typeof rawValue.adminEmail === "string" ? rawValue.adminEmail : "",
    adminName: typeof rawValue.adminName === "string" ? rawValue.adminName : "",
  };
}

function normalizeUser(payload: unknown): AuthenticatedUser {
  if (!isRecord(payload)) {
    throw new AuthApiError("Ungueltige Antwort vom Auth-Service");
  }

  const id = payload.id;
  const email = payload.email;
  const name = payload.name ?? payload.fullName;
  const company = payload.company ?? payload.companyName;
  const mustChangePassword = payload.mustChangePassword ?? payload.must_change_password;

  if (
    (typeof id !== "string" && typeof id !== "number") ||
    typeof email !== "string" ||
    typeof name !== "string"
  ) {
    throw new AuthApiError("Benutzerdaten vom Auth-Service sind unvollstaendig");
  }

  return {
    id: String(id),
    email,
    name,
    company: typeof company === "string" ? company : undefined,
    role: mapRole(payload.role),
    mustChangePassword: mustChangePassword === true,
    impersonation: normalizeImpersonation(payload.impersonation),
  };
}

function getNestedPayload(payload: unknown) {
  if (!isRecord(payload)) {
    return null;
  }

  const nestedPayload = payload.data ?? payload.user;
  return nestedPayload ?? null;
}

function extractUser(payload: unknown): AuthenticatedUser | null {
  const userPayload = getNestedPayload(payload) ?? payload;

  try {
    return normalizeUser(userPayload);
  } catch {
    return null;
  }
}

function extractToken(payload: unknown): string | null {
  if (!isRecord(payload)) {
    return null;
  }

  const directToken = payload.token ?? payload.accessToken;
  if (typeof directToken === "string" && directToken.trim()) {
    return directToken;
  }

  const nestedPayload = getNestedPayload(payload);
  if (!isRecord(nestedPayload)) {
    return null;
  }

  const nestedToken = nestedPayload.token ?? nestedPayload.accessToken;
  return typeof nestedToken === "string" && nestedToken.trim() ? nestedToken : null;
}

async function requestJson<T>(input: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(input, {
    ...init,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(init.headers || {}),
    },
  });

  const rawText = await response.text();
  let payload: unknown = null;

  if (rawText) {
    try {
      payload = JSON.parse(rawText);
    } catch {
      if (!response.ok) {
        throw new AuthApiError("Authentifizierung fehlgeschlagen", response.status);
      }

      throw new AuthApiError("Der Auth-Service hat keine gueltige JSON-Antwort geliefert");
    }
  }

  if (!response.ok) {
    throw new AuthApiError(
      getErrorMessage(payload, "Authentifizierung fehlgeschlagen"),
      response.status,
    );
  }

  return payload as T;
}

function getAuthorizationHeader(token: string | null | undefined) {
  if (!token) {
    return {};
  }

  return {
    Authorization: `Bearer ${token}`,
  };
}

export function readStoredToken() {
  if (typeof window === "undefined") {
    return null;
  }

  return localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
}

export function readStoredUser() {
  if (typeof window === "undefined") {
    return null;
  }

  const rawUser = localStorage.getItem(AUTH_USER_STORAGE_KEY);
  if (!rawUser) {
    return null;
  }

  try {
    return normalizeUser(JSON.parse(rawUser));
  } catch {
    clearStoredSession();
    return null;
  }
}

export function storeSession(session: AuthSession) {
  if (typeof window === "undefined") {
    return;
  }

  if (session.token) {
    localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, session.token);
  } else {
    localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
  }

  localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(session.user));
}

export function clearStoredSession() {
  if (typeof window === "undefined") {
    return;
  }

  localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
  localStorage.removeItem(AUTH_USER_STORAGE_KEY);
}

export function readStoredImpersonationBackup() {
  if (typeof window === "undefined") {
    return null;
  }

  const token = localStorage.getItem(AUTH_IMPERSONATION_BACKUP_TOKEN_STORAGE_KEY);
  const rawUser = localStorage.getItem(AUTH_IMPERSONATION_BACKUP_USER_STORAGE_KEY);
  if (!token || !rawUser) {
    return null;
  }

  try {
    return {
      token,
      user: normalizeUser(JSON.parse(rawUser)),
    } satisfies AuthSession;
  } catch {
    clearStoredImpersonationBackup();
    return null;
  }
}

export function storeImpersonationBackup(session: AuthSession) {
  if (typeof window === "undefined") {
    return;
  }

  if (!session.token) {
    throw new AuthApiError("Die Admin-Sitzung kann ohne Token nicht gesichert werden");
  }

  localStorage.setItem(AUTH_IMPERSONATION_BACKUP_TOKEN_STORAGE_KEY, session.token);
  localStorage.setItem(AUTH_IMPERSONATION_BACKUP_USER_STORAGE_KEY, JSON.stringify(session.user));
}

export function clearStoredImpersonationBackup() {
  if (typeof window === "undefined") {
    return;
  }

  localStorage.removeItem(AUTH_IMPERSONATION_BACKUP_TOKEN_STORAGE_KEY);
  localStorage.removeItem(AUTH_IMPERSONATION_BACKUP_USER_STORAGE_KEY);
}

export async function fetchCurrentUser(token?: string | null) {
  const payload = await requestJson<unknown>(getAuthEndpoint(CURRENT_USER_PATH), {
    method: "GET",
    headers: getAuthorizationHeader(token),
  });

  const user = extractUser(payload);
  if (!user) {
    throw new AuthApiError("Der aktuelle Benutzer konnte nicht geladen werden");
  }

  return user;
}

export async function restoreCurrentUser(token?: string | null) {
  const payload = await requestJson<unknown>(getAuthEndpoint(SESSION_PATH), {
    method: "GET",
    headers: getAuthorizationHeader(token),
  });

  return extractUser(payload);
}

export async function loginWithPassword(email: string, password: string) {
  const payload = await requestJson<unknown>(getAuthEndpoint(LOGIN_PATH), {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });

  const token = extractToken(payload);
  const user = extractUser(payload) ?? (await fetchCurrentUser(token));

  return { token, user };
}

export async function updateOwnPassword(password: string, token?: string | null) {
  const payload = await requestJson<unknown>(getAuthEndpoint(PASSWORD_PATH), {
    method: "PATCH",
    headers: getAuthorizationHeader(token),
    body: JSON.stringify({ password }),
  });

  const user = extractUser(payload);
  if (!user) {
    throw new AuthApiError("Das Passwort konnte nicht gespeichert werden");
  }

  return user;
}

export async function impersonateCustomerAccount(userId: string, adminToken?: string | null) {
  const normalizedUserId = typeof userId === "string" ? userId.trim() : "";
  if (!normalizedUserId) {
    throw new AuthApiError("Kundenkonto konnte nicht gewechselt werden");
  }

  if (!adminToken) {
    throw new AuthApiError("Die aktuelle Admin-Sitzung ist nicht mehr verfügbar");
  }

  const adminUser = await fetchCurrentUser(adminToken);
  if (adminUser.role !== "admin") {
    throw new AuthApiError("Nur Admins können in eine Kundenansicht wechseln");
  }

  storeImpersonationBackup({
    token: adminToken,
    user: adminUser,
  });

  try {
    const payload = await requestJson<unknown>(
      getAuthEndpoint(`${ADMIN_IMPERSONATE_BASE_PATH}/${encodeURIComponent(normalizedUserId)}/impersonate`),
      {
        method: "POST",
        headers: getAuthorizationHeader(adminToken),
      },
    );

    const token = extractToken(payload);
    const user = extractUser(payload) ?? (await fetchCurrentUser(token));

    if (!token || !user) {
      throw new AuthApiError("Kundenansicht konnte nicht geöffnet werden");
    }

    return { token, user } satisfies AuthSession;
  } catch (error) {
    clearStoredImpersonationBackup();
    throw error;
  }
}

export async function restoreAdminSessionFromImpersonation() {
  const backupSession = readStoredImpersonationBackup();
  if (!backupSession?.token) {
    throw new AuthApiError("Die ursprüngliche Admin-Sitzung ist nicht mehr verfügbar");
  }

  const user = await fetchCurrentUser(backupSession.token);
  return {
    token: backupSession.token,
    user,
  } satisfies AuthSession;
}

export async function requestAccountSetupEmail(email: string) {
  const payload = await requestJson<unknown>(getAuthEndpoint(ACCOUNT_SETUP_REQUEST_PATH), {
    method: "POST",
    body: JSON.stringify({ email }),
  });

  if (!isRecord(payload) || typeof payload.message !== "string") {
    throw new AuthApiError("Der Link zur Passwortvergabe konnte nicht angefordert werden");
  }

  return payload.message;
}

export async function fetchAccountSetupState(token: string) {
  const payload = await requestJson<unknown>(
    `${getAuthEndpoint(ACCOUNT_SETUP_TOKEN_PATH)}?token=${encodeURIComponent(token)}`,
    {
      method: "GET",
    },
  );

  if (!isRecord(payload) || !isRecord(payload.setup)) {
    throw new AuthApiError("Der Aktivierungslink ist ungültig");
  }

  const setup = payload.setup;

  if (
    typeof setup.customerName !== "string" ||
    typeof setup.customerEmail !== "string" ||
    typeof setup.mustSetPassword !== "boolean"
  ) {
    throw new AuthApiError("Der Aktivierungslink ist ungültig");
  }

  return {
    customerName: setup.customerName,
    customerEmail: setup.customerEmail,
    projectTitle: typeof setup.projectTitle === "string" ? setup.projectTitle : null,
    orderAcceptedAt: typeof setup.orderAcceptedAt === "string" ? setup.orderAcceptedAt : null,
    paymentStatus: typeof setup.paymentStatus === "string" ? setup.paymentStatus : null,
    isPaymentConfirmed: setup.isPaymentConfirmed === true,
    mustSetPassword: setup.mustSetPassword,
  } satisfies AccountSetupState;
}

export async function completeAccountSetup(token: string, password: string) {
  const payload = await requestJson<unknown>(getAuthEndpoint(ACCOUNT_SETUP_COMPLETE_PATH), {
    method: "POST",
    body: JSON.stringify({ token, password }),
  });

  if (!isRecord(payload) || !isRecord(payload.setup)) {
    throw new AuthApiError("Das Passwort konnte nicht gespeichert werden");
  }

  const setup = payload.setup;

  if (
    typeof setup.customerName !== "string" ||
    typeof setup.customerEmail !== "string" ||
    typeof setup.mustSetPassword !== "boolean"
  ) {
    throw new AuthApiError("Das Passwort konnte nicht gespeichert werden");
  }

  return {
    customerName: setup.customerName,
    customerEmail: setup.customerEmail,
    projectTitle: typeof setup.projectTitle === "string" ? setup.projectTitle : null,
    orderAcceptedAt: typeof setup.orderAcceptedAt === "string" ? setup.orderAcceptedAt : null,
    paymentStatus: typeof setup.paymentStatus === "string" ? setup.paymentStatus : null,
    isPaymentConfirmed: setup.isPaymentConfirmed === true,
    mustSetPassword: setup.mustSetPassword,
  } satisfies AccountSetupState;
}

export { AuthApiError };
